import React, { useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import PaginationTable from "../../pagination/Pagination4";
const ICDSSaleFPSReport = ({
  month,
  year,
  allotMonth,
  allotYear,
  distMonthName,
  alotMonthName,
  distCode,
  icdsCommodityData,
  distName,
  afsoCode,
  afsoName,
}) => {
  const [loading, setLoading] = useState([]);
  const [submitMessage, setSubmitMessage] = useState("");
  const [icdsFPSData, setICDSFPSData] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchICDSFPSSales = async () => {
      try {
        setSubmitMessage("");
        setLoading(true);
        const icdsFPSResponse = await fetch(
          `${process.env.REACT_APP_URL}/api/icds/getFPSWiseICDSDetails?month=${month}&year=${year}&alotMonth=${allotMonth}&alotYear=${allotYear}&distCode=${distCode}&afsoCode=${afsoCode}`
        );
        if (!icdsFPSResponse.ok) {
          throw new Error("Failed to fetch FPS Details");
        }
        const data = await icdsFPSResponse.json();
        setICDSFPSData(data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchICDSFPSSales();
  }, [month, year, allotMonth, allotYear, distCode, afsoCode]);

  const totals = icdsFPSData.reduce(
    (acc, item) => {
      acc.totalSchools += Number(item.total_school) || 0;
      acc.totalEntitlement += Number(item.total_entitlement) || 0;

      icdsCommodityData.forEach((commodity) => {
        const availedKey = `availed_aws_comm_${commodity.commId}`;
        const qtyKey = `total_availed_qty_comm_${commodity.commId}`;

        if (!acc.commodities) acc.commodities = {};
        if (!acc.commodities[commodity.commId]) {
          acc.commodities[commodity.commId] = { totalAvailed: 0, totalQty: 0 };
        }

        acc.commodities[commodity.commId].totalAvailed +=
          Number(item[availedKey]) || 0;
        acc.commodities[commodity.commId].totalQty += Number(item[qtyKey]) || 0;
      });

      acc.count += 1;

      return acc;
    },
    {
      totalSchools: 0,
      totalEntitlement: 0,
      commodities: {},
      count: 0,
    }
  );

  const handleDownloadExcel = async (
    FPSData,
    commodityData,
    totals,
    distMonthName,
    year,
    allotMonthName,
    allotYear,
    distName,
    afsoName
  ) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("ICDS FPS Sales Report");

    // === Set Column Widths ===
    const columnWidths = [
      8, // Sl No
      22, // FPS
      12, // AWS     
    ];

    // Commodity columns (2 per commodity)
    commodityData.forEach(() => {
      columnWidths.push(15); // Availed AWS
      columnWidths.push(20); // Availed Qty
    });

    worksheet.columns = columnWidths.map((width) => ({ width }));

    // === Calculate total columns dynamically ===
    const totalColumns = 3 + commodityData.length * 2; // SlNo + FPS + Total(AWS) + commodity sale columns

    // === Title Row ===
    worksheet.mergeCells(1, 1, 1, totalColumns);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = `ICDS Sales for District:${distName} ,Block:${afsoName}, Distribution (Month'Year): ${distMonthName}'${year} and Allotment (Month'Year): ${allotMonthName}'${allotYear}`;
    titleCell.font = { size: 12, bold: true };
    titleCell.alignment = { horizontal: "center" };

    // === Header Rows ===
    // Row 2: Sl No, District, Total columns merged
    worksheet.mergeCells(2, 1, 4, 1); // Sl No
    worksheet.getCell("A2").value = "Sl No";

    worksheet.mergeCells(2, 2, 4, 2); // FPS
    worksheet.getCell("B2").value = "FPS";

    worksheet.mergeCells(2, 3, 3, 3); // Total ( AWS)
    worksheet.getCell("C2").value = "Total";

    // Commodity Sale Header
    worksheet.mergeCells(2, 4, 2, totalColumns);
    worksheet.getCell("F2").value = "Sale";

    // Row 3: Commodity Names
    let col = 4;
    commodityData.forEach((commodity) => {
      worksheet.mergeCells(3, col, 3, col + 1);
      worksheet.getCell(3, col).value = commodity.commName;
      col += 2;
    });

    // Row 4: Subheaders
    worksheet.getCell("C4").value = "AWS";

    col = 4;
    commodityData.forEach(() => {
      worksheet.getCell(4, col).value = "Availed AWS";
      worksheet.getCell(4, col + 1).value = "Availed Qty (Kgs/Lts)";
      col += 2;
    });

    // Apply header styles
    [2, 3, 4].forEach((rowNumber) => {
      const row = worksheet.getRow(rowNumber);
      row.font = { bold: true };
      row.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFDDDDDD" },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // === Data Rows ===
    FPSData.forEach((item, index) => {
      const rowValues = [
        index + 1,
        item.fps_id,
        item.total_school,
      ];

      commodityData.forEach((commodity) => {
        rowValues.push(item[`availed_aws_comm_${commodity.commId}`]);
        rowValues.push(
          item[`total_availed_qty_comm_${commodity.commId}`].toFixed(3)
        );
      });

      const row = worksheet.addRow(rowValues);

      // Align numeric columns
      for (let i = 3; i <= rowValues.length; i++) {
        row.getCell(i).alignment = { horizontal: "right" };
      }
    });

    // === Total Row ===
    const totalRowValues = [
      "",
      "Total",
      totals.totalSchools,
    ];

    commodityData.forEach((commodity) => {
      totalRowValues.push(
        totals.commodities[commodity.commId]?.totalAvailed || 0
      );
      totalRowValues.push(
        (totals.commodities[commodity.commId]?.totalQty || 0).toFixed(3)
      );
    });

    const totalRow = worksheet.addRow(totalRowValues);
    totalRow.font = { bold: true };
    totalRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "double" },
        right: { style: "thin" },
      };
      cell.alignment =
        colNumber <= 2 ? { horizontal: "left" } : { horizontal: "right" };
    });

    // === Download Excel ===
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `ICDS_FPS_Sales_Report.xlsx`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value === "all" ? icdsFPSData.length : parseInt(value, 10));
    setPage(0);
  };

  const filteredData = icdsFPSData.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return Object.values(item).some(
      (value) => value && value.toString().toLowerCase().includes(query)
    );
  });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <>
      {!loading && icdsCommodityData.length > 0 ? (
        <div style={{ justifyContent: "center", marginBottom: "70px" }}>
          <div
            style={{
              width: "50%",
              margin: "0 auto 20px auto",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <PaginationTable
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <input
            type="text"
            placeholder="Search "
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: "8px", width: "200px" ,marginLeft:"200px"}}
            />

                    <div>
 
        </div>

          </div>
          <table width="82%" border="1" cellPadding="5" cellSpacing="0" style={{marginLeft: "240px" }}>
            <thead style={{ position: "sticky", top: 0 }}>
              <tr className="tableheader">
                <th colSpan={9 + icdsCommodityData.length * 4}>
                  ICDS Sales for District:{distName},Block : {afsoName},
                  Distribution (Month'Year) : {distMonthName}'{year} and
                  Allotment (Month'Year) : {alotMonthName}'{allotYear}
                </th>
              </tr>
              <tr className="tableheader">
                <th rowSpan={3} >Sl No</th>
                <th rowSpan={3}>FPS</th>
                <th rowSpan={2} colSpan={1}>
                  Total
                </th>
                <th colSpan={icdsCommodityData.length * 2}>Sale</th>
              </tr>

              {/* Commodity Names */}
              <tr className="tableheader">
                {icdsCommodityData.map((commodity, index) => (
                  <th key={index} colSpan={2}>
                    {commodity.commName}
                  </th>
                ))}
              </tr>

              <tr className="tableheader">
                <th>AWS</th>
             

                {icdsCommodityData.map((commodity, index) => (
                  <React.Fragment key={index}>
                    <th>
                      Availed
                      <br />
                      AWS
                    </th>
                    <th>
                      Availed
                      <br />
                      Qty
                      <br />
                      (Kgs/Lts)
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={index}>
                  <td style={{ textAlign: "center" }}>
                    {rowsPerPage === "all"
                      ? index + 1
                      : index + 1 + page * rowsPerPage}
                  </td>
                  <td> {item.fps_id}</td>
                  <td style={{ textAlign: "right" }}>{item.total_school}</td>
                  

                  {icdsCommodityData.map((commodity) => (
                    <React.Fragment key={commodity.commId}>
                      <td style={{ textAlign: "right" }}>
                        {item[`availed_aws_comm_${commodity.commId}`]}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {item[
                          `total_availed_qty_comm_${commodity.commId}`
                        ].toFixed(3)}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: "bold" }} className="tableheader">
                <td colSpan={2} style={{ textAlign: "center" }}>
                  Total
                </td>
                <td style={{ textAlign: "right" }}>{totals.totalSchools}</td>
               

                {icdsCommodityData.map((commodity) => (
                  <React.Fragment key={commodity.commId}>
                    <td style={{ textAlign: "right" }}>
                      {totals.commodities[commodity.commId]?.totalAvailed}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {totals.commodities[commodity.commId]?.totalQty.toFixed(
                        3
                      )}
                    </td>
                  </React.Fragment>
                ))}
              </tr>
            </tfoot>
          </table>

          <div style={{ marginTop: "20px", textAlign: "center" }}>
            {
              <button
                onClick={() =>
                  handleDownloadExcel(
                    icdsFPSData,
                    icdsCommodityData,
                    totals,
                    distMonthName,
                    year,
                    alotMonthName,
                    allotYear,
                    distName,
                    afsoName
                  )
                }
                className="button button2"
              >
                Download Excel
              </button>
            }
          </div>
        </div>
      ) : (
        <div className="mainhead" align="center">
          {" "}
          No data found{" "}
        </div>
      )}
    </>
  );
};

export default ICDSSaleFPSReport;
